import { useState } from 'react';
import { useVerticalTabsContext } from 'further-ui/components/VerticalTabsContext';

type Result<T extends string> = {
  selectedTab: T;
  setSelectedTab: (tab: T) => void;
  tabs: undefined;
};

type ResultWithTabs<TTab extends { id: string }> = {
  selectedTab: TTab['id'];
  setSelectedTab: (tab: TTab['id']) => void;
  tabs: Array<TTab & { dotColor: string | undefined }>;
};

export function useVerticalTabs<T extends string>(initial: T): Result<T>;

export function useVerticalTabs<TTab extends { id: string }>(
  initial: TTab['id'],
  options: {
    tabs: Array<TTab>;
    getDotColor?: (tab: TTab['id']) => string | undefined;
  },
): ResultWithTabs<TTab>;

export function useVerticalTabs<TTab extends { id: string }>(
  initial: TTab['id'],
  options?: {
    tabs?: Array<TTab>;
    getDotColor?: (tab: TTab['id']) => string | undefined;
  },
): Result<TTab['id']> | ResultWithTabs<TTab> {
  // can either use the context or local state
  const context = useVerticalTabsContext();
  const [selectedTab, setSelectedTab] = useState(initial);

  const resolvedTab =
    (context?.verticalTab as TTab['id'] | undefined) ?? selectedTab;

  const setter = (tab: TTab['id']) => {
    setSelectedTab(tab);
    context?.setVerticalTab(tab);
  };

  if (options?.tabs) {
    const tabs = options.tabs.map((tab) => ({
      ...tab,
      dotColor: options.getDotColor?.(tab.id),
    }));
    return { selectedTab: resolvedTab, setSelectedTab: setter, tabs };
  }

  return { selectedTab: resolvedTab, setSelectedTab: setter, tabs: undefined };
}
